import React, { useEffect } from 'react';

import { Checkbox } from '@rbilabs/components-library/build/components/checkbox';
import { isEqual, pick, pickBy } from 'lodash-es';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IDeliveryAddress } from 'generated/rbi-graphql';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';

const Disclaimer = styled.span`
  font-weight: normal;
`;

const SaveAddress = styled.span`
  font-weight: bold;
  font-size: 0.75rem;
`;

/**
 * @name getNormalizedAddress
 * @description
 * Picks specific address values we want to compare when determining equality and removes
 * any properties that have `null`, `undefined` or `''` values.
 * @param {IDeliveryAddress & { shouldSave?: boolean }} address
 * @example
 * getNormalizedAddress({
 *  addressLine1: '123 Drury Ln',
 *  addressLine2: '',
 *  city: null,
 *  zip: undefined
 * }); // { addressLine1: '123 Drury Ln' }
 */
const getNormalizedAddress = (address: IDeliveryAddress & { shouldSave?: boolean }) => {
  return pickBy<string>(
    pick(address, 'addressLine1', 'addressLine2', 'city', 'state', 'zip', 'country', 'phoneNumber')
  );
};

export const deliveryAddressIsSaved = (
  addresses: IDeliveryAddress[],
  address: IDeliveryAddress & { shouldSave?: boolean }
): boolean => {
  const foundAddress = addresses.find(userAddress => {
    return isEqual(getNormalizedAddress(address), getNormalizedAddress(userAddress));
  });

  return Boolean(foundAddress);
};

const SaveAddressSelection: React.FC = () => {
  const { formatMessage } = useIntl();
  const { user } = useAuthContext();
  const { deliveryAddress, orderPhoneNumber, updateShouldSaveDeliveryAddress } = useOrderContext();
  const enableNewSaveAddress = useFlag(LaunchDarklyFlag.ENABLE_SAVE_DELIVERY_ADDRESS);

  const addresses = user?.details?.deliveryAddresses || [];

  // if the user selected a saved address, show the option to save as disabled and already checked
  const isUsingSavedAddress = deliveryAddressIsSaved(addresses, {
    ...deliveryAddress,
    phoneNumber: orderPhoneNumber,
  });

  const SaveAddressLabel = (
    <SaveAddress>
      {formatMessage(
        { id: 'saveAddressLabel' },
        { disclaimer: <Disclaimer>{formatMessage({ id: 'saveAddressDisclaimer' })}</Disclaimer> }
      )}
    </SaveAddress>
  );

  useEffect(() => {
    if (!enableNewSaveAddress && isUsingSavedAddress) {
      // if a previously saved address is used, send shouldSave as true
      // in order to ensure the latest address value is updated
      updateShouldSaveDeliveryAddress(true);
    }
  }, [enableNewSaveAddress, isUsingSavedAddress, updateShouldSaveDeliveryAddress]);

  const handleSaveAddressClick = () => {
    updateShouldSaveDeliveryAddress(!deliveryAddress.shouldSave);
  };

  return (
    <div>
      <Checkbox
        data-testid="save-address-selection"
        checked={deliveryAddress.shouldSave}
        onChange={handleSaveAddressClick}
        label={SaveAddressLabel}
        name="saveAddressSelection"
        disabled={isUsingSavedAddress}
      />
    </div>
  );
};

export default SaveAddressSelection;
